<script setup>
import { inject, ref, onMounted, watch, computed } from 'vue'
import { useForm } from 'vee-validate'
import slugify from 'slugify'
import { useI18n } from 'vue-i18n'

import Services from './services'
import API from '@/js/api'

const { t } = useI18n()
const dataset = inject('dataset')
const theform = ref()
const isLoading = ref(false)
const data = ref(null)
const submission = ref({})
const model = ref({})

const serverResponseError = ref(null)

const fieldKey = (field) =>
  field.clean_name || slugify(field.label || '', { lower: true, strict: true })

const beforeunloadHandler = (event) => {
  event.preventDefault()
}

const { handleSubmit, setErrors, errors: formErrors } = useForm()

const onSuccess = async () => {
  const data = new FormData(theform.value)
  let response = null

  serverResponseError.value = null
  isLoading.value = true
  try {
    response = await API.post(dataset.post, { body: data })
  } finally {
    isLoading.value = false
  }

  if (response?.status === 502) {
    serverResponseError.value = t('form.serverError')
    document
      .querySelector('#server-error')
      .scrollIntoView({ behavior: 'smooth' })

    return
  }

  if (response.submission_pk && response.next_url) {
    window.removeEventListener('beforeunload', beforeunloadHandler)
    window.location.href = response.next_url
  } else if (response) {
    setErrors(response)
    onInvalidSubmit()
  }
}

const onInvalidSubmit = () => {
  const firstErrorFieldName = Object.keys(formErrors.value)[0]
  const el = document
    .querySelector(`[name="${firstErrorFieldName}"]`)
    .closest('.field')

  if (el) {
    el.scrollIntoView({ behavior: 'smooth' })
  }
}

const onSubmit = handleSubmit(onSuccess, onInvalidSubmit)

onMounted(async () => {
  data.value = await API.get(dataset.api)

  if (dataset.submission) {
    submission.value = await API.getSubmission(dataset.submission)

    Object.entries(submission.value.form_data).forEach(([key, value]) => {
      const e = data.value.form_fields.find((e) => fieldKey(e) == key)

      if (e) {
        const keyName = fieldKey(e)
        if (['file', 'multifile'].includes(e.field_type)) {
          if (e.field_type == 'file')
            model.value[keyName] = { name: submission.value.files[value[0]] }
          else {
            model.value[keyName] = value.map((f) => ({
              name: submission.value.files[f],
            }))
          }
        } else {
          model.value[keyName] = value
        }
      }
    })
  } else {
    data.value.form_fields.forEach((e) => {
      model.value[fieldKey(e)] = Services.fieldDefaults[e.field_type]
    })
  }

  window.addEventListener('beforeunload', beforeunloadHandler)
})
</script>

<template>
  <form
    v-if="data"
    @submit.prevent="onSubmit(onSuccess, onInvalidSubmit)"
    ref="theform"
    novalidate
  >
    <template v-for="field in data.form_fields">
      <template v-if="field.field_type != 'section_title'">
        <div
          :label="field.field_type != 'checkbox' ? field.label : null"
          class="field is-size-6"
          :class="{
            'is-required': field.required,
            'has-errors':
              formErrors && formErrors.hasOwnProperty(fieldKey(field)),
          }"
        >
          <label class="label pl-3">{{ field.label }}</label>
          <div
            class="content pl-3 is-size-9"
            v-html="field.help_text"
            v-if="field.help_text"
          />

          <component
            v-if="Services.formComponents[field.field_type]"
            :is="Services.formComponents[field.field_type]"
            v-bind="{
              field: { ...field, clean_name: fieldKey(field) },
            }"
            v-model="model[fieldKey(field)]"
          />

          <div class="columns" v-if="field.image">
            <div class="column is-6">
              <figure class="image">
                <img src="field.image" alt="Form image" class="is-rounded" />
              </figure>
            </div>
          </div>

          <div
            class="error is-size-9 pr-7 pl-3"
            v-if="formErrors && formErrors.hasOwnProperty(fieldKey(field))"
            v-text="formErrors[fieldKey(field)]"
          />
        </div>
      </template>

      <div class="field is-section" v-else>
        <h2
          class="is-size-4"
          :id="'ID_' + slugify(field.label, { lower: true })"
          v-text="field.label"
        />

        <div
          class="content mt-6"
          v-html="field.help_text"
          v-if="field.help_text"
        />

        <div class="columns mt-3" v-if="field.image">
          <div class="column is-6">
            <figure class="image">
              <img :src="field.image" alt="Form image" class="is-rounded" />
            </figure>
          </div>
        </div>
      </div>
    </template>

    <b-button
      nativeType="submit"
      :label="$t('form.submit')"
      :loading="isLoading"
      type="is-primary"
      class="is-medium is-size-6 is-wide mt-7"
      rounded
    />

    <div id="server-error">
      <div
        class="field has-errors mt-5 has-text-danger is-size-6"
        v-if="serverResponseError"
        v-text="serverResponseError"
      />
    </div>
  </form>
</template>
